import { InputAdornment } from "@mui/material";
import {
	CoreButton,
	CoreMenuItem,
	CoreModal,
	CoreModalDescription,
	CoreSelectTextField,
	CoreTextField,
} from "@verity/components/core";
import { type FocusEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY, countryDialCodes, countryLabel } from "./countries";
import type { DemoLead } from "./lead-api";

const LEAD_ROLE_OPTIONS = [
	"Executive Leadership",
	"IT or LMS Administration",
	"Procurement or Operations",
	"Instructional Content Designer",
	"Instructor",
	"Student",
	"Other",
] as const;

export type LeadFormDraft = {
	firstName: string;
	lastName: string;
	organization: string;
	role: string;
	country: string;
	dialCode: string;
	phone: string;
	email: string;
};

// The text inputs the form validates natively. The selects are not here: a MUI select's focusout
// target is not an input, so role is tracked on its own handlers, and country and dial code always
// have a value.
const LEAD_FIELD_LABELS: Record<string, string> = {
	firstName: "First name",
	lastName: "Last name",
	organization: "Organization",
	phone: "Phone number",
	email: "Email",
};

// Native validation messages vary between browsers and never mention the formats we require, so
// state the requirement instead. These render through the core field's own error appearance rather
// than a browser bubble.
function leadFieldError(input: HTMLInputElement): string | undefined {
	if (input.validity.valid) return undefined;
	if (input.name === "phone") return "Enter a valid phone number.";
	const label = LEAD_FIELD_LABELS[input.name] ?? "This field";
	if (input.validity.valueMissing) return `${label} is required.`;
	switch (input.name) {
		case "email":
			return "Enter a valid email address.";
		default:
			return `Enter a valid ${label.toLowerCase()}.`;
	}
}

function LeadForm({
	submitting,
	error,
	draft,
	onSubmit,
}: {
	submitting: boolean;
	error?: string;
	draft?: LeadFormDraft;
	onSubmit: (lead: DemoLead, draft: LeadFormDraft) => void;
}) {
	const [role, setRole] = useState(draft?.role ?? "");
	const [country, setCountry] = useState(draft?.country ?? DEFAULT_COUNTRY);
	const [dialCode, setDialCode] = useState(() => draft?.dialCode ?? countryDialCodes(DEFAULT_COUNTRY)[0]);
	const dialCodes = countryDialCodes(country);
	const [formValid, setFormValid] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
	const formRef = useRef<HTMLFormElement>(null);

	const updateFormValidity = useCallback(() => setFormValid(formRef.current?.checkValidity() ?? false), []);
	// The role select updates a hidden input outside the form's change events; recheck after it renders.
	useEffect(() => {
		updateFormValidity();
	}, [role, updateFormValidity]);

	const setFieldError = useCallback((name: string, message: string | undefined) => {
		setFieldErrors((current) => (current[name] === message ? current : { ...current, [name]: message }));
	}, []);

	// Errors surface on blur rather than on every keystroke, so the form doesn't scold someone
	// halfway through typing a value that is merely incomplete. focusout bubbles, so one handler on
	// the form covers every input.
	const showFieldError = (event: FocusEvent<HTMLFormElement>) => {
		const input = event.target;
		if (input instanceof HTMLInputElement && input.name in LEAD_FIELD_LABELS) {
			setFieldError(input.name, leadFieldError(input));
		}
	};

	// Once a field is showing an error, track it on every keystroke so a correction clears it
	// immediately instead of waiting for the next blur.
	const handleChange = (event: FormEvent<HTMLFormElement>) => {
		updateFormValidity();
		const input = event.target;
		if (input instanceof HTMLInputElement && fieldErrors[input.name]) {
			setFieldError(input.name, leadFieldError(input));
		}
	};

	const submit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const data = new FormData(event.currentTarget);
		const field = (name: string) => String(data.get(name) ?? "").trim();
		const phone = field("phone");
		onSubmit(
			{
				firstName: field("firstName"),
				lastName: field("lastName"),
				email: field("email"),
				organization: field("organization"),
				role,
				phoneNumber: `+${dialCode}${phone}`,
			},
			{
				firstName: field("firstName"),
				lastName: field("lastName"),
				organization: field("organization"),
				role,
				country,
				dialCode,
				phone,
				email: field("email"),
			},
		);
	};

	return (
		<>
			{error ? (
				<p className="lead-form-error" role="alert">
					{error}
				</p>
			) : null}
			<form
				ref={formRef}
				className="lead-form"
				aria-labelledby="lead-form-title"
				onSubmit={submit}
				onChange={handleChange}
				onBlur={showFieldError}
			>
				<div className="lead-form-fields">
					<div className="lead-form-field">
						<span id="lead-first-name-label" className="lead-form-label">
							First Name
						</span>
						<CoreTextField
							id="lead-first-name"
							name="firstName"
							autoComplete="given-name"
							placeholder="First name"
							defaultValue={draft?.firstName}
							maxLength={100}
							required
							fullWidth
							ariaLabelledBy="lead-first-name-label"
							error={fieldErrors.firstName}
						/>
					</div>
					<div className="lead-form-field">
						<span id="lead-last-name-label" className="lead-form-label">
							Last Name
						</span>
						<CoreTextField
							id="lead-last-name"
							name="lastName"
							autoComplete="family-name"
							placeholder="Last name"
							defaultValue={draft?.lastName}
							maxLength={100}
							required
							fullWidth
							ariaLabelledBy="lead-last-name-label"
							error={fieldErrors.lastName}
						/>
					</div>
					<div className="lead-form-field">
						<span id="lead-organization-label" className="lead-form-label">
							Organization
						</span>
						<CoreTextField
							id="lead-organization"
							name="organization"
							autoComplete="organization"
							placeholder="Organization name"
							defaultValue={draft?.organization}
							maxLength={255}
							required
							fullWidth
							ariaLabelledBy="lead-organization-label"
							error={fieldErrors.organization}
						/>
					</div>
					<div className="lead-form-field">
						<span id="lead-role-label" className="lead-form-label">
							Role
						</span>
						<CoreSelectTextField<string>
							id="lead-role"
							name="role"
							required
							value={role}
							onChange={(event) => {
								setRole(String(event.target.value));
								setFieldError("role", undefined);
							}}
							onBlur={() => setFieldError("role", role ? undefined : "Role is required.")}
							fullWidth
							ariaLabelledBy="lead-role-label"
							popperMaxHeight="320px"
							renderValue={(value) => value || "Select your role"}
							error={fieldErrors.role}
						>
							{LEAD_ROLE_OPTIONS.map((option) => (
								<CoreMenuItem key={option} value={option}>
									{option}
								</CoreMenuItem>
							))}
						</CoreSelectTextField>
					</div>
					<div className="lead-form-field">
						<span id="lead-country-label" className="lead-form-label">
							Country/Region
						</span>
						<CoreSelectTextField<string>
							id="lead-country"
							name="country"
							required
							value={country}
							onChange={(event) => {
								const next = String(event.target.value);
								setCountry(next);
								setDialCode(countryDialCodes(next)[0]);
							}}
							fullWidth
							ariaLabelledBy="lead-country-label"
							popperMaxHeight="320px"
							renderValue={countryLabel}
						>
							{COUNTRY_OPTIONS.map((option) => (
								<CoreMenuItem key={option.name} value={option.name}>
									{countryLabel(option.name)}
								</CoreMenuItem>
							))}
						</CoreSelectTextField>
					</div>
					<div className="lead-form-field">
						<span id="lead-phone-label" className="lead-form-label">
							Phone Number
						</span>
						<div className="lead-phone-inputs">
							{dialCodes.length > 1 ? (
								<CoreSelectTextField<string>
									id="lead-dial-code"
									name="dialCode"
									label="Country code"
									required
									value={dialCode}
									onChange={(event) => setDialCode(String(event.target.value))}
									className="lead-dial-code"
									renderValue={(value) => `+${value}`}
								>
									{dialCodes.map((code) => (
										<CoreMenuItem key={code} value={code}>
											+{code}
										</CoreMenuItem>
									))}
								</CoreSelectTextField>
							) : null}
							<CoreTextField
								id="lead-phone"
								name="phone"
								type="tel"
								autoComplete="tel-national"
								placeholder="Phone number"
								defaultValue={draft?.phone}
								maxLength={15}
								required
								fullWidth
								className="lead-phone-number"
								ariaLabelledBy="lead-phone-label"
								inputProps={{ inputMode: "numeric", pattern: "[0-9]{5,15}" }}
								InputProps={
									dialCodes.length > 1
										? undefined
										: { startAdornment: <InputAdornment position="start">{`+${dialCode}`}</InputAdornment> }
								}
								error={fieldErrors.phone}
							/>
						</div>
					</div>
					<div className="lead-form-field lead-form-field-wide">
						<span id="lead-email-label" className="lead-form-label">
							Email
						</span>
						<CoreTextField
							id="lead-email"
							name="email"
							type="email"
							autoComplete="email"
							placeholder="Email"
							defaultValue={draft?.email}
							maxLength={254}
							spellCheck={false}
							required
							fullWidth
							ariaLabelledBy="lead-email-label"
							inputProps={{ inputMode: "email" }}
							error={fieldErrors.email}
						/>
					</div>
				</div>
				<CoreButton
					className="lead-form-submit"
					size="medium"
					type="submit"
					disabled={submitting || !formValid || !role}
				>
					{submitting ? "Preparing" : "Continue"}
				</CoreButton>
			</form>
		</>
	);
}

export default function DemoLeadModal({
	submitting,
	error,
	draft,
	container,
	onClose,
	onSubmit,
}: {
	submitting: boolean;
	error?: string;
	draft?: LeadFormDraft;
	/**
	 * The preview frame. The dialog is portalled into it rather than to `document.body`, which is what
	 * lets styles.css pin it — and its backdrop — to that frame, so only the video dims and the hero
	 * above it stays lit.
	 */
	container: HTMLElement | null;
	onClose: () => void;
	onSubmit: (lead: DemoLead, draft: LeadFormDraft) => void;
}) {
	return (
		<CoreModal
			open
			container={container}
			onClose={onClose}
			maxHeight="100%"
			modalMargin="var(--demo-modal-margin, 16px)"
			ariaLabel="Explore Verity"
			width="min(620px, 100%)"
			maxWidth="620px"
		>
			<div className="lead-modal-body">
				<div className="lead-modal-heading">
					<h2 id="lead-form-title" className="lead-modal-title">
						Explore Verity
					</h2>
					<CoreModalDescription>
						Provide details about yourself to personalize your Verity experience.
					</CoreModalDescription>
				</div>
				<LeadForm submitting={submitting} error={error} draft={draft} onSubmit={onSubmit} />
			</div>
		</CoreModal>
	);
}
